import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import API from '../../services/api';

export default function ViewInvoiceDetail({ order }) {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchDetails = async () => {
      if (!order?.id) { setRows([]); return; }
      try {
        const res = await API.get('order-Av/unified_products/', {
          params: { order_ids: order.id }
        });

        const details = (res?.data?.order_details || []).map(d => ({
          key: `P-${d.product?.id}`,
          isBonus: false,
          code: d.product?.code ?? '',
          description: d.product?.description ?? '',
          ordered: d.quantity ?? 0,
          taken: d.taken_quantity ?? 0,
          remaining: d.remaining_quantity ?? 0,
        }));

        const bonuses = (res?.data?.order_bonus || []).map(b => ({
          key: `B-${b.product?.id}`,
          isBonus: true,
          code: b.product?.code ?? '',
          description: b.product?.description ?? '',
          ordered: b.quantity ?? 0,
          taken: b.taken_quantity ?? 0,
          remaining: b.remaining_quantity ?? 0,
        }));

        // Puedes ordenar si quieres que los bonus vayan al final/inicio:
        const merged = [...details, ...bonuses]; // o [...bonuses, ...details]
        setRows(merged);
      } catch (e) {
        console.error('Error obteniendo detalle de factura', e);
        setRows([]);
      }
    };
    fetchDetails();
  }, [order]);

  const filteredRows = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    if (!term) return rows;
    return (rows || []).filter((r) => {
      const code = String(r?.code || '').toLowerCase();
      const description = String(r?.description || '').toLowerCase();
      return code.includes(term) || description.includes(term);
    });
  }, [rows, searchTerm]);

  return (
    <Box>
      <TextField
        size="small"
        variant="standard"
        fullWidth
        placeholder="Buscar por código o descripción..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 140 }}>Código</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="right" style={{ width: 120 }}>Ordenado</TableCell>
              <TableCell align="right" style={{ width: 120 }}>Facturado</TableCell>
              <TableCell align="right" style={{ width: 120 }}>Pendiente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">Sin datos</TableCell>
              </TableRow>
            )}

            {filteredRows.map((r) => (
              <TableRow
                key={r.key}
                sx={(theme) =>
                  r.isBonus
                    ? {
                        backgroundColor: alpha(theme.palette.success.light, 0.15),
                        '&:hover': { backgroundColor: alpha(theme.palette.success.light, 0.25) },
                      }
                    : {}
                }
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {r.code}
                    {r.isBonus && (
                      <Tooltip title="Bonificación">
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                          label="BONUS"
                          sx={{ height: 22 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {r.description}
                  </Box>
                </TableCell>

                <TableCell align="right">{r.ordered}</TableCell>
                <TableCell align="right">{r.taken}</TableCell>
                <TableCell align="right">{r.remaining}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
